// =============================================================================
// changelogUtils.js - ULTRA VERBOSE DEBUG VERSION
// =============================================================================
// This version has EXTENSIVE logging to help diagnose any issues
// Once working, you can switch to the regular version
// =============================================================================

import { db, auth } from './firebase';
import { collection, addDoc, serverTimestamp, query, where, orderBy, limit as firestoreLimit, getDocs, doc, getDoc } from 'firebase/firestore';

/**
 * Log an allocation change to the changelog
 */
export const logAllocationChange = async (clubId, changeType, allocationData, allocationType) => {
  console.log('🚀 ========== CHANGELOG FUNCTION CALLED ==========');
  console.log('📝 Function: logAllocationChange');
  console.log('📝 clubId:', clubId);
  console.log('📝 changeType:', changeType);
  console.log('📝 allocationType:', allocationType);
  console.log('📝 allocationData:', JSON.stringify(allocationData, null, 2));

  try {
    // Step 1: Validate inputs
    console.log('🔍 Step 1: Validating inputs...');
    
    if (!clubId) {
      console.error('❌ VALIDATION FAILED: Missing clubId');
      throw new Error('Club ID is required');
    }
    console.log('✅ clubId valid:', clubId);
    
    if (!changeType || !['created', 'updated', 'deleted'].includes(changeType)) {
      console.error('❌ VALIDATION FAILED: Invalid changeType:', changeType);
      throw new Error('Change type must be created, updated, or deleted');
    }
    console.log('✅ changeType valid:', changeType);
    
    if (!allocationData) {
      console.error('❌ VALIDATION FAILED: Missing allocationData');
      throw new Error('Allocation data is required');
    }
    console.log('✅ allocationData present');

    console.log('✅ All validation passed!');

    // Step 2: Get current user info
    console.log('🔍 Step 2: Getting current user info...');
    const currentUser = auth.currentUser;
    console.log('👤 auth.currentUser:', currentUser ? `User ID: ${currentUser.uid}` : 'null (not logged in)');
    
    let userName = 'System';
    let userRole = 'system';
    
    if (currentUser) {
      console.log('👤 Fetching user document from Firestore...');
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        console.log('👤 User doc path:', `users/${currentUser.uid}`);
        
        const userDoc = await getDoc(userDocRef);
        console.log('👤 User doc exists:', userDoc.exists());
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('👤 User data:', JSON.stringify(userData, null, 2));
          
          userName = userData.displayName || userData.email || currentUser.email || 'Unknown User';
          userRole = userData.role || 'user';
          
          console.log('👤 Extracted userName:', userName);
          console.log('👤 Extracted userRole:', userRole);
        } else {
          console.warn('⚠️ User document does not exist, using defaults');
        }
      } catch (userError) {
        console.error('❌ Error fetching user info:', userError);
        console.log('⚠️ Will use default user info');
      }
    } else {
      console.log('⚠️ No current user, using System defaults');
    }
    
    console.log('✅ User info resolved: userName =', userName, ', userRole =', userRole);

    // Step 3: Get current date/time
    console.log('🔍 Step 3: Getting current date/time...');
    const now = new Date();
    const actionDate = now.toISOString().split('T')[0];
    const actionTime = now.toTimeString().split(' ')[0].substring(0, 5);
    console.log('📅 actionDate:', actionDate);
    console.log('⏰ actionTime:', actionTime);

    // Step 4: Format sections
    console.log('🔍 Step 4: Formatting sections...');
    console.log('📋 allocationData.sections:', allocationData.sections);
    console.log('📋 sections type:', Array.isArray(allocationData.sections) ? 'array' : typeof allocationData.sections);
    
    let sectionString;
    if (Array.isArray(allocationData.sections)) {
      sectionString = allocationData.sections.sort().join(', ');
      console.log('📋 Formatted sections (from array):', sectionString);
    } else {
      sectionString = allocationData.section || '';
      console.log('📋 Using section string directly:', sectionString);
    }

    // Step 5: Create changelog entry
    console.log('🔍 Step 5: Creating changelog entry object...');
    const changelogEntry = {
      action: changeType,
      type: allocationType || allocationData.type || 'training',
      timestamp: serverTimestamp(),
      
      // Allocation details
      team: allocationData.team || allocationData.teamName,
      section: sectionString,
      
      // Date/time information
      date: allocationData.date,
      allocationDate: allocationData.date,
      time: allocationData.startTime || allocationData.slot || allocationData.timeSlot,
      
      // Action tracking
      actionDate: actionDate,
      actionTime: actionTime,
      
      // User information
      user: userName,
      userRole: userRole,
      
      // Additional metadata
      clubId,
      pitch: allocationData.pitch || allocationData.pitchId,
      pitchName: allocationData.pitchName || allocationData.pitch,
      duration: allocationData.duration,
      startTime: allocationData.startTime || allocationData.slot,
      endTime: allocationData.endTime,
      createdAt: new Date().toISOString(),
    };

    console.log('📋 Changelog entry prepared:');
    console.log(JSON.stringify(changelogEntry, null, 2));

    // Step 6: Write to Firestore
    console.log('🔍 Step 6: Writing to Firestore...');
    const changelogPath = `clubs/${clubId}/changelog`;
    console.log('📍 Collection path:', changelogPath);
    
    try {
      const changelogRef = collection(db, 'clubs', clubId, 'changelog');
      console.log('📍 Collection reference created');
      
      console.log('💾 Calling addDoc...');
      const docRef = await addDoc(changelogRef, changelogEntry);
      console.log('✅ addDoc successful!');
      console.log('📄 Document ID:', docRef.id);
      console.log('📄 Document path:', docRef.path);
      
      console.log('✅ ========== CHANGELOG CREATED SUCCESSFULLY ==========');
      console.log('🎉 Entry ID:', docRef.id);
      
      return docRef.id;
    } catch (firestoreError) {
      console.error('❌ ========== FIRESTORE WRITE FAILED ==========');
      console.error('❌ Error code:', firestoreError.code);
      console.error('❌ Error message:', firestoreError.message);
      console.error('❌ Full error:', firestoreError);
      throw firestoreError;
    }
    
  } catch (error) {
    console.error('❌ ========== CHANGELOG FUNCTION FAILED ==========');
    console.error('❌ Error type:', error.constructor.name);
    console.error('❌ Error message:', error.message);
    console.error('❌ Error code:', error.code);
    console.error('❌ Error stack:', error.stack);
    console.error('❌ Full error object:', error);
    throw error;
  }
};

/**
 * Get changelog entries for a club
 */
export const getChangelogEntries = async (clubId, options = {}) => {
  console.log('📖 ========== GET CHANGELOG ENTRIES ==========');
  console.log('📖 clubId:', clubId);
  console.log('📖 options:', options);
  
  try {
    const { limit = 50, startDate, endDate, changeType } = options;
    
    console.log('📍 Collection path:', `clubs/${clubId}/changelog`);
    const changelogRef = collection(db, 'clubs', clubId, 'changelog');
    
    console.log('🔍 Building query...');
    let q = query(changelogRef, orderBy('timestamp', 'desc'));
    
    if (startDate) {
      console.log('📅 Adding startDate filter:', startDate);
      q = query(q, where('date', '>=', startDate));
    }
    
    if (endDate) {
      console.log('📅 Adding endDate filter:', endDate);
      q = query(q, where('date', '<=', endDate));
    }
    
    if (changeType) {
      console.log('🔍 Adding changeType filter:', changeType);
      q = query(q, where('action', '==', changeType));
    }
    
    if (limit) {
      console.log('🔢 Adding limit:', limit);
      q = query(q, firestoreLimit(limit));
    }
    
    console.log('💾 Executing query...');
    const snapshot = await getDocs(q);
    console.log('✅ Query successful');
    console.log('📊 Documents found:', snapshot.size);
    
    const entries = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('✅ Retrieved entries:', entries.length);
    console.log('📋 First entry:', entries[0]);
    
    return entries;
  } catch (error) {
    console.error('❌ Error getting changelog entries:', error);
    throw error;
  }
};

export default {
  logAllocationChange,
  getChangelogEntries
};
